import React, { useState, useEffect } from 'react';
import { BookOpen, Users, ChevronRight, Clock, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { instancesAPI } from '@/services/api';
import { toast } from 'sonner';
import { useTranslation } from "react-i18next";

const ChaptersTab = ({ instanceId, onChapterClick }) => {
  const { t } = useTranslation();
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!instanceId) {
      setChapters([]);
      setLoading(false);
      return;
    }

    const fetchChapters = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('📚 Fetching chapters for instance:', instanceId);
        const chapters = await instancesAPI.getChapters(instanceId);
        
        console.log('✅ Chapters loaded:', chapters);
        setChapters(chapters || []);
      } catch (error) {
        console.error('❌ Error loading chapters:', error);
        setError(error.message);
        
        // Show toast notification
        toast.error(t('chapters.load_failed'), {
          description: error.message || t('chapters.try_again_desc')
        });
      } finally {
        setLoading(false);
      }
    };

    fetchChapters();
  }, [instanceId]);

  const handleChapterClick = (chapter) => {
    console.log('📖 Chapter clicked:', chapter);
    
    // Call the onChapterClick prop to open the ProfilePage
    // Pass the full chapter object as an entity
    if (onChapterClick) {
      onChapterClick(chapter);
    }
  };

  if (!instanceId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-8">
        <div className="text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
            <BookOpen className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('chapters.no_instance_title')}</h2>
          <p className="text-gray-600">{t('chapters.no_instance_desc')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4">
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg">
                <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="flex flex-col items-center justify-center min-h-[40vh]">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full mx-auto mb-4 flex items-center justify-center">
              <FileText className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('chapters.error_loading')}</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button
              onClick={() => window.location.reload()}
              variant="outline"
              size="sm"
            >
              {t('common.try_again')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (chapters.length === 0) {
    return (
      <div className="p-4">
        <div className="flex flex-col items-center justify-center min-h-[40vh]">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4 flex items-center justify-center">
              <BookOpen className="w-8 h-8 text-blue-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('chapters.no_chapters_title')}</h3>
            <p className="text-gray-600 mb-4">
              {t('chapters.no_chapters_desc')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-4">
        <h2 className="text-xl font-bold text-gray-900 mb-2">{t('chapters.title')}</h2>
        <p className="text-sm text-gray-600">
          {t('chapters.available_count', { count: chapters.length })}
        </p>
      </div>

      <div className="space-y-3">
        {chapters.map((chapter, index) => (
          <div
            key={chapter.id}
            className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm cursor-pointer transition-all"
            onClick={() => handleChapterClick(chapter)}
          >
            {/* Chapter Icon */}
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              {chapter.type === 'chapter' ? (
                <BookOpen className="w-6 h-6 text-blue-600" />
              ) : (
                <FileText className="w-6 h-6 text-blue-600" />
              )}
            </div>

            {/* Chapter Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-medium text-gray-900 truncate">
                  {chapter.name}
                </h3>
                <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                  {chapter.type}
                </span>
              </div>
              
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  <span>{t('chapters.posts_count', { count: chapter.posts_count })}</span>
                </div>
                
                {chapter.chapter_number && (
                  <div className="flex items-center gap-1">
                    <span>{t('chapters.ch_label', { count: chapter.chapter_number })}</span>
                    {chapter.section_index && (
                      <span>, {t('chapters.sec_label', { count: chapter.section_index })}</span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Arrow */}
            <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChaptersTab;